import React, { useRef, useState, useCallback, useEffect } from "react";
import { PageHeaderWrapper } from "@ant-design/pro-layout";
import { Row, Col, Card, Modal, message } from "antd";
import { connect } from "dva";
import { getUrlParams } from "@/utils/utils";
import StrategyCard from "./Components/StrategyCard";
import {
  EntityType,
  fetchDealerStrategyByType,
  fetchTransporterStrategyByType,
  dealerStrategyInitialParamsByType,
  transporterStrategyInitialParamsByType,
} from "./constants";

const grid = { xxl: { span: 12 }, sm: { span: 24 } };

const Strategies = ({
  configPerm,

  strategyId,
  settingsConcurrencyStamp,
  dispatch,

  loadingDealer,
  dealerStrategyType,
  dealerStrategyInherited,
  dealerStrategy,
  draftDealerStrategy,

  loadingTransporter,
  transporterStrategyType,
  transporterStrategyInherited,
  transporterStrategy,
  draftTransporterStrategy,

  groupView,
}) => {
  const lastCheckedDealerStrategyType = useRef(null);
  const lastCheckedTransporterStrategyType = useRef(null);

  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(null);

  const onSubmit = useCallback(
    ({ submitDealerStrategy, submitTransporterStrategy }) => {
      let newDealerStrategy = null;
      let newTransporterStrategy = null;

      if (submitDealerStrategy) {
        newDealerStrategy = {
          ...dealerStrategy,
          ...draftDealerStrategy,
          referenceId: getUrlParams("referenceId"),
        };
      }

      if (submitTransporterStrategy) {
        newTransporterStrategy = {
          ...transporterStrategy,
          ...draftTransporterStrategy,
        };
      }

      dispatch({
        type: "pricingStrategies/submitStrategies",
        dealerStrategy: newDealerStrategy,
        transporterStrategy: newTransporterStrategy,
      }).then(({ dealerError, transporterError }) => {
        let dealerWentOk = submitDealerStrategy && dealerError === null;
        let transporterWentOk = submitTransporterStrategy && transporterError === null;

        if (dealerWentOk && transporterWentOk) {
          message.info("Strategies successfully updated");

          // We do not care about the error because an error message
          // is automatically triggered when something went wrong with
          // one of these, then only display successful messages
        } else if (dealerWentOk && !transporterWentOk) {
          message.info("Dealer pricing strategy was successfully updated");
        } else if (!dealerWentOk && transporterWentOk) {
          message.info("Transporter pricing strategy was successfully updated");
        }
      });
    },
    [dealerStrategy, draftDealerStrategy, transporterStrategy, draftTransporterStrategy],
  );

  const handleDealerStrategyChange = useCallback(
    newValues => {
      const strategy = {
        ...draftDealerStrategy,
        ...newValues,
      };

      dispatch({
        type: "pricingStrategies/dealerSetDraftStrategy",
        strategy,
      });
    },
    [draftDealerStrategy],
  );

  const handleTransporterStrategyChange = useCallback(
    newValues => {
      const strategy = {
        ...draftTransporterStrategy,
        ...newValues,
      };

      dispatch({
        type: "pricingStrategies/transporterSetDraftStrategy",
        strategy,
      });
    },
    [draftTransporterStrategy],
  );

  const handleDealerStrategyTypeChange = useCallback(
    newStrategyType => {
      let type = fetchDealerStrategyByType[newStrategyType];

      if (lastCheckedDealerStrategyType.current === newStrategyType || type === null || type === undefined) {
        return;
      }

      let extraInitialParams = dealerStrategyInitialParamsByType[newStrategyType];
      const firstTime = lastCheckedDealerStrategyType.current === null;
      lastCheckedDealerStrategyType.current = newStrategyType;

      dispatch({
        type,
        referenceId: getUrlParams("referenceId"),
        ...extraInitialParams,
      });

      if (!firstTime) {
        dispatch({
          type: "pricingStrategies/setSettings",
          strategyId,
          settingsConcurrencyStamp,
          dealerStrategyType: newStrategyType,
          transporterStrategyType,
        });
      }
    },
    [strategyId, settingsConcurrencyStamp, transporterStrategyType],
  );

  const handleTransporterStrategyTypeChange = useCallback(
    newStrategyType => {
      let type = fetchTransporterStrategyByType[newStrategyType];

      if (lastCheckedTransporterStrategyType.current === newStrategyType || type === null || type === undefined) {
        return;
      }

      let extraInitialParams = transporterStrategyInitialParamsByType[newStrategyType];
      const firstTime = lastCheckedTransporterStrategyType.current === null;
      lastCheckedTransporterStrategyType.current = newStrategyType;

      dispatch({
        type,
        referenceId: getUrlParams("referenceId"),
        ...extraInitialParams,
      });

      if (!firstTime) {
        dispatch({
          type: "pricingStrategies/setSettings",
          strategyId,
          settingsConcurrencyStamp,
          dealerStrategyType,
          transporterStrategyType: newStrategyType,
        });
      }
    },
    [strategyId, settingsConcurrencyStamp, dealerStrategyType],
  );

  const handleConfirmDiscardChanges = useCallback(() => {
    const typeParser = {
      [EntityType.Dealer]: "pricingStrategies/discardDealerDraftStrategy",
      [EntityType.Transporter]: "pricingStrategies/discardDraftTransporterStrategy",
    };

    const type = typeParser[showDiscardChangesModal];

    if (type !== undefined) {
      dispatch({ type });
    }

    setShowDiscardChangesModal(null);
  }, [showDiscardChangesModal]);

  const handleCancelDiscardChanges = useCallback(() => {
    setShowDiscardChangesModal(null);
  }, []);

  const handleDealerStrategySubmit = useCallback(() => {
    onSubmit({
      submitDealerStrategy: true,
      submitTransporterStrategy: false,
    });
  }, [onSubmit]);

  const handleTransporterStrategySubmit = useCallback(() => {
    onSubmit({
      submitDealerStrategy: false,
      submitTransporterStrategy: true,
    });
  }, [onSubmit]);

  useEffect(() => {
    // fetch current settings
    if (configPerm["PricingService.PricingSettings"]) {
      dispatch({
        type: "pricingStrategies/getSettings",
        referenceId: getUrlParams("referenceId"),
      }).then(success => {
        if (!success) {
          message.error("Something went wrong getting the current pricing strategies settings");
        }
      });
    }
  }, [configPerm?.["PricingService.PricingSettings"]]);

  useEffect(() => {
    // after fetching settings dealerStrategyType will change and trigger this
    handleDealerStrategyTypeChange(dealerStrategyType);
  }, [handleDealerStrategyTypeChange, dealerStrategyType]);

  useEffect(() => {
    // after fetching settings transporterStrategyType will change and trigger this
    handleTransporterStrategyTypeChange(transporterStrategyType);
  }, [handleTransporterStrategyTypeChange, transporterStrategyType]);

  useEffect(() => {
    if (dealerStrategy !== null && draftDealerStrategy === null) {
      dispatch({
        type: "pricingStrategies/setDealerDraftStrategy",
        referenceId: getUrlParams("referenceId"),
        strategyType: dealerStrategy.strategyType,
        payload: dealerStrategy,
      });
    }

    if (transporterStrategy !== null && draftTransporterStrategy === null) {
      dispatch({
        type: "pricingStrategies/setTransporterDraftStrategy",
        referenceId: getUrlParams("referenceId"),
        strategyType: transporterStrategy.strategyType,
        payload: transporterStrategy,
      });
    }
  }, [dealerStrategy, transporterStrategy, draftDealerStrategy, draftTransporterStrategy]);

  return (
    <PageHeaderWrapper onBack={() => window.history.back()} title="Pricing Strategies">
      <Card bordered={false}>
        <Row gutter={[16, 16]} justify="space-between">
          <Col {...grid}>
            <StrategyCard
              type={EntityType.Dealer}
              loading={loadingDealer}
              inherited={dealerStrategyInherited && !groupView}
              strategyType={draftDealerStrategy?.strategyType}
              strategy={dealerStrategy}
              draftStrategy={draftDealerStrategy}
              onStrategyTypeChange={handleDealerStrategyTypeChange}
              onStrategyChange={handleDealerStrategyChange}
              onSubmit={handleDealerStrategySubmit}
              onDiscard={() => setShowDiscardChangesModal(EntityType.Dealer)}
              dispatch={dispatch}
            />
          </Col>
          <Col {...grid}>
            <StrategyCard
              type={EntityType.Transporter}
              loading={loadingTransporter}
              inherited={transporterStrategyInherited && !groupView}
              strategyType={draftTransporterStrategy?.strategyType}
              strategy={transporterStrategy}
              draftStrategy={draftTransporterStrategy}
              onStrategyTypeChange={handleTransporterStrategyTypeChange}
              onStrategyChange={handleTransporterStrategyChange}
              onSubmit={handleTransporterStrategySubmit}
              onDiscard={() => setShowDiscardChangesModal(EntityType.Transporter)}
              dispatch={dispatch}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title="Confirm discard changes"
        okText="Discard changes"
        okButtonProps={{
          danger: true,
        }}
        visible={showDiscardChangesModal !== null}
        onOk={handleConfirmDiscardChanges}
        onCancel={handleCancelDiscardChanges}
      >
        <span>Are you sure you want to discard all changes?</span>
        <br />
        <span>This action cannot be undone.</span>
      </Modal>
    </PageHeaderWrapper>
  );
};

export default connect(({ loading, pricingStrategies, configs }) => {
  const bothLoading = loading.effects["pricingStrategies/getSettings"];

  return {
    configPerm: configs.configPerm,
    strategyId: pricingStrategies.strategyId,
    settingsConcurrencyStamp: pricingStrategies.settingsConcurrencyStamp,

    dealerStrategyType: pricingStrategies.dealerStrategyType,
    dealerStrategyInherited: pricingStrategies.dealerStrategyInherited,
    dealerStrategy: pricingStrategies.dealerStrategy,
    draftDealerStrategy: pricingStrategies.draftDealerStrategy,
    loadingDealer:
      bothLoading ||
      loading.effects["pricingStrategies/dealerGetStrategyVariable"] ||
      loading.effects["pricingStrategies/dealerGetStrategyResidential"] ||
      loading.effects["pricingStrategies/dealerGetStrategyPricingBands"] ||
      loading.effects["pricingStrategies/dealerGetStrategyFixedByZip"] ||
      loading.effects["pricingStrategies/dealerFixedByZipAddRow"] ||
      loading.effects["pricingStrategies/dealerFixedByZipUpdateRow"] ||
      loading.effects["pricingStrategies/dealerFixedByZipDeleteRow"] ||
      loading.effects["pricingStrategies/dealerGetStrategyCostPlus"] ||
      loading.effects["pricingStrategies/dealerGetStrategyNissanCityToCity"],

    transporterStrategyType: pricingStrategies.transporterStrategyType,
    transporterStrategyInherited: pricingStrategies.transporterStrategyInherited,
    transporterStrategy: pricingStrategies.transporterStrategy,
    draftTransporterStrategy: pricingStrategies.draftTransporterStrategy,
    loadingTransporter: bothLoading,
  };
})(Strategies);
